import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { getMeta, refreshData } from "../lib/api";

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export const SyncStatus = () => {
  const qc = useQueryClient();
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: getMeta });
  const [refreshing, setRefreshing] = useState(false);
  const isLive = meta?.source === "api-sports";

  const handleRefresh = async () => {
    if (refreshing) return;
    try {
      setRefreshing(true);
      await refreshData();
      toast.success("Refreshing live data…", { description: "This can take up to a minute." });
      setTimeout(() => {
        qc.invalidateQueries();
        setRefreshing(false);
        toast.success("Data updated");
      }, 45000);
    } catch (e) {
      setRefreshing(false);
      toast.error("Couldn't refresh right now");
    }
  };

  return (
    <div data-testid="sync-status" className="hidden lg:flex items-center gap-3">
      <div className="flex items-center gap-2 px-3 h-9 border border-border bg-secondary">
        <span className={`w-2 h-2 rounded-full ${isLive ? "bg-primary live-dot" : "bg-muted-foreground"}`} />
        <span className="text-xs font-semibold uppercase tracking-wider">
          {isLive ? "Live" : "Sample"}
        </span>
        <span className="text-xs text-muted-foreground">· {timeAgo(meta?.syncedAt)}</span>
      </div>
      <button
        data-testid="refresh-data-btn"
        onClick={handleRefresh}
        disabled={refreshing}
        title="Refresh live data"
        className="w-9 h-9 flex items-center justify-center border border-border hover:border-primary hover:text-primary transition-colors duration-150 disabled:opacity-50"
      >
        <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
      </button>
    </div>
  );
};
